import React, { useState, useRef, useCallback } from 'react';
import { DiagramPage, DiagramType } from '../types';
import { ThemePalette } from '../types';

interface PageManagerProps {
  pages: DiagramPage[];
  activePage: string;
  linkNodeId?: string | null;
  readOnly?: boolean;
  onSwitchPage: (pageId: string) => void;
  onLinkNodeToPage?: (nodeId: string, pageId: string) => void;
  onAddPage: (diagramType?: DiagramType) => void;
  onRenamePage: (pageId: string, name: string) => void;
  onDeletePage: (pageId: string) => void;
  onSetPageDiagramType?: (pageId: string, type: DiagramType) => void;
  palette: ThemePalette;
}

export const PageManager: React.FC<PageManagerProps> = ({
  pages, activePage, linkNodeId, readOnly = false, onSwitchPage, onLinkNodeToPage, onAddPage, onRenamePage, onDeletePage, onSetPageDiagramType, palette,
}) => {
  const [editingPageId, setEditingPageId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [pos, setPos] = useState({ x: 16, y: 80 });
  const [isDragging, setIsDragging] = useState(false);
  const dragOrigin = useRef<{ mx: number; my: number; px: number; py: number } | null>(null);

  const handleHeaderMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragOrigin.current = { mx: e.clientX, my: e.clientY, px: pos.x, py: pos.y };
    setIsDragging(true);

    const onMove = (ev: MouseEvent) => {
      if (!dragOrigin.current) return;
      setPos({
        x: dragOrigin.current.px + ev.clientX - dragOrigin.current.mx,
        y: dragOrigin.current.py + ev.clientY - dragOrigin.current.my,
      });
    };
    const onUp = () => {
      setIsDragging(false);
      dragOrigin.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [pos]);

  const startRename = (page: DiagramPage) => {
    setEditingPageId(page.id);
    setEditingName(page.name);
  };

  const commitRename = () => {
    if (editingPageId && editingName.trim()) {
      onRenamePage(editingPageId, editingName.trim());
    }
    setEditingPageId(null);
    setEditingName('');
  };

  const glass: React.CSSProperties = {
    background: palette.panelBg,
    borderColor: palette.panelBorder,
    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
  };

  return (
    <div
      className="absolute z-[90] w-52 rounded-2xl border backdrop-blur-md select-none"
      style={{ ...glass, left: pos.x, top: pos.y, cursor: isDragging ? 'grabbing' : 'default' }}
    >
      {/* Header — drag handle */}
      <div
        className="flex items-center justify-between px-3 py-2 rounded-t-2xl cursor-grab"
        style={{ borderBottom: `1px solid ${palette.panelBorder}` }}
        onMouseDown={handleHeaderMouseDown}
      >
        <span className="text-xs font-semibold" style={{ color: palette.panelText }}>
          {linkNodeId ? 'Link to page...' : 'Pages'}
        </span>
        {!readOnly && (
          <div className="flex items-center gap-1" onMouseDown={e => e.stopPropagation()}>
            <button
              onClick={() => onAddPage('flow')}
              className="px-1.5 h-5 rounded-md flex items-center justify-center text-[9px] font-bold hover:opacity-80 transition-opacity"
              style={{ background: `${palette.panelBorder}40`, color: palette.panelText }}
              title="Add flow page"
            >+ Flow</button>
            <button
              onClick={() => onAddPage('sequence')}
              className="px-1.5 h-5 rounded-md flex items-center justify-center text-[9px] font-bold hover:opacity-80 transition-opacity"
              style={{ background: `${palette.panelBorder}40`, color: palette.panelText }}
              title="Add sequence page"
            >+ Seq</button>
          </div>
        )}
      </div>

      {/* Page list */}
      <div className="py-1 max-h-64 overflow-y-auto">
        {pages.map(page => (
          <div
            key={page.id}
            className="flex items-center gap-1 px-2 py-1.5 mx-1 rounded-lg transition-colors cursor-pointer group"
            style={{
              background: page.id === activePage ? `${palette.panelBorder}40` : 'transparent',
            }}
            onClick={() => {
              if (linkNodeId && onLinkNodeToPage) {
                onLinkNodeToPage(linkNodeId, page.id);
              } else {
                onSwitchPage(page.id);
              }
            }}
          >
            {/* Active indicator */}
            <span
              className="w-1.5 h-1.5 rounded-full flex-shrink-0 transition-opacity"
              style={{
                background: '#818cf8',
                opacity: page.id === activePage ? 1 : 0,
              }}
            />

            {/* Name / inline edit */}
            {editingPageId === page.id ? (
              <input
                autoFocus
                value={editingName}
                onChange={e => setEditingName(e.target.value)}
                onBlur={commitRename}
                onKeyDown={e => {
                  if (e.key === 'Enter') commitRename();
                  if (e.key === 'Escape') { setEditingPageId(null); setEditingName(''); }
                  e.stopPropagation();
                }}
                onClick={e => e.stopPropagation()}
                className="flex-1 bg-transparent border-b outline-none text-xs px-0"
                style={{ color: palette.panelText, borderColor: palette.panelBorder }}
              />
            ) : (
              <span
                className="flex-1 text-xs truncate"
                style={{ color: page.id === activePage ? palette.panelText : palette.panelSubText }}
                  onDoubleClick={e => { if (!readOnly) { e.stopPropagation(); startRename(page); } }}
              >
                {page.name}
              </span>
            )}
            {/* Diagram type badge — click to toggle */}
            {!readOnly && onSetPageDiagramType && editingPageId !== page.id && (
              <button
                className="flex-shrink-0 px-1 h-4 rounded text-[8px] font-semibold transition-opacity"
                style={{
                  background: (page.diagramType ?? 'flow') === 'sequence' ? '#0891b220' : '#4f46e520',
                  color: (page.diagramType ?? 'flow') === 'sequence' ? '#22d3ee' : '#818cf8',
                  border: `1px solid ${(page.diagramType ?? 'flow') === 'sequence' ? '#22d3ee40' : '#818cf840'}`,
                }}
                title={`Type: ${page.diagramType ?? 'flow'} — click to switch`}
                onClick={e => {
                  e.stopPropagation();
                  onSetPageDiagramType(page.id, (page.diagramType ?? 'flow') === 'flow' ? 'sequence' : 'flow');
                }}
              >
                {(page.diagramType ?? 'flow') === 'sequence' ? 'SEQ' : 'FLOW'}
              </button>
            )}

            {/* Actions — visible on hover */}
            {!readOnly && editingPageId !== page.id && (
              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  className="w-4 h-4 flex items-center justify-center rounded text-[9px] hover:opacity-80"
                  style={{ color: palette.panelSubText }}
                  title="Rename"
                  onClick={e => { e.stopPropagation(); startRename(page); }}
                >
                  Edit
                </button>
                {pages.length > 1 && (
                  <button
                    className="w-4 h-4 flex items-center justify-center rounded text-[9px] hover:text-red-400 transition-colors"
                    style={{ color: palette.panelSubText }}
                    title="Delete page"
                    onClick={e => {
                      e.stopPropagation();
                      if (window.confirm(`Delete page "${page.name}"?`)) onDeletePage(page.id);
                    }}
                  >
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Footer hint */}
      <div
        className="px-3 py-1.5 text-[9px] rounded-b-2xl"
        style={{ color: palette.panelSubText, borderTop: `1px solid ${palette.panelBorder}20` }}
      >
        {readOnly ? 'Read-only page navigation' : 'Dbl-click name to rename'}
      </div>
    </div>
  );
};
